import { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackContext } from '@sg/garnish';
import { AddLineItemToCartSuccess, EditLineItemInCartSuccess } from '@sg/graphql-schema';

// ─────────────────────────────────────────────────────────────────────────────

export const useCartMutationErrorNotice = (responseData: LineItemErrorResponseData) => {
  const errorMessage = responseData?.message;
  const responseType = responseData?.__typename;

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── Errors ────────────────────────────────────────────────────────────────

  const isRestaurantMaxQuantityExceeded = responseType === 'RestaurantMaxQuantityExceeded'; // prettier-ignore
  const isRestaurantMaxDeliveryQuantityExceeded = responseType === 'RestaurantMaxDeliveryQuantityExceeded'; // prettier-ignore
  const isMaxQuantityErrorOccurred = isRestaurantMaxQuantityExceeded || isRestaurantMaxDeliveryQuantityExceeded; // prettier-ignore
  const isMaxModificationLimitExceeded = responseType === 'MaxModificationsExceeded'; // prettier-ignore
  const hasValidationErrors = responseType === 'ValidationError';
  const hasProductOutOfStockErrors = responseType === 'ProductOutOfStock';
  const isLocationClosed = responseType === 'LocationClosed';
  const isLineItemModificationInvalid = responseType === 'LineItemModificationsInvalidException'; // prettier-ignore
  const isLoyaltyScanTokenExpired = responseType === 'LoyaltyScanTokenExpired'; // prettier-ignore
  const isOrderLocked = responseType === 'OrderLocked';
  const isUnknownError = responseType && responseType !== 'AddLineItemToCartSuccess' && responseType !== 'EditLineItemInCartSuccess'; // prettier-ignore

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const handleRestaurantMaxQuantityError = useCallback(() => {
    if (!errorMessage) return;

    addNoticeBanner({ text: errorMessage, palette: 'caution' });
  }, [errorMessage, addNoticeBanner]);

  const handleMaxModificationLimitExceededError = useCallback(() => {
    if (!errorMessage) return;

    addNoticeBanner({ text: errorMessage, palette: 'neutral' });
  }, [errorMessage, addNoticeBanner]);

  const handleValidationErrors = useCallback(() => {
    const fieldErrorMessage = responseData?.fieldErrors?.[0]?.message;

    if (!fieldErrorMessage) return;

    addNoticeBanner({ text: fieldErrorMessage, palette: 'caution' });
  }, [responseData, addNoticeBanner]);

  const handleProductOutOfStockError = useCallback(() => {
    if (!errorMessage) return;

    addNoticeBanner({ text: errorMessage, palette: 'caution' });
  }, [errorMessage, addNoticeBanner]);

  const handleLocationClosedError = useCallback(() => {
    if (!errorMessage) return;

    addNoticeBanner({ text: errorMessage, palette: 'caution' });
  }, [errorMessage, addNoticeBanner]);

  const handleLineItemModificationInvalidError = useCallback(() => {
    if (!errorMessage) return;

    addNoticeBanner({ text: errorMessage, palette: 'caution' });
  }, [errorMessage, addNoticeBanner]);

  const handleLoyaltyScanTokenExpiredError = useCallback(() => {
    addNoticeBanner({
      text: formatMessage(messages.loyaltySessionExpired),
      palette: 'caution',
    });
  }, [formatMessage, addNoticeBanner]);

  const handleOrderLockedError = useCallback(() => {
    addNoticeBanner({ text: formatMessage(messages.orderLocked), palette: 'neutral' });
  }, [formatMessage, addNoticeBanner]);

  const handleUnknownError = useCallback(() => {
    addNoticeBanner({
      text: errorMessage ?? formatMessage(messages.genericError),
      palette: 'caution',
    });
  }, [errorMessage, formatMessage, addNoticeBanner]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isMaxQuantityErrorOccurred) return handleRestaurantMaxQuantityError();
    if (isMaxModificationLimitExceeded) return handleMaxModificationLimitExceededError();
    if (isLineItemModificationInvalid) return handleLineItemModificationInvalidError();
    if (hasValidationErrors) return handleValidationErrors();
    if (hasProductOutOfStockErrors) return handleProductOutOfStockError();
    if (isLocationClosed) return handleLocationClosedError();
    if (isLoyaltyScanTokenExpired) return handleLoyaltyScanTokenExpiredError();
    if (isOrderLocked) return handleOrderLockedError();
    if (isUnknownError) return handleUnknownError();
  }, [
    addNoticeBanner,
    responseData,
    isMaxModificationLimitExceeded,
    isRestaurantMaxDeliveryQuantityExceeded,
    isRestaurantMaxQuantityExceeded,
    hasValidationErrors,
    isMaxQuantityErrorOccurred,
    isLocationClosed,
    hasProductOutOfStockErrors,
    handleRestaurantMaxQuantityError,
    handleMaxModificationLimitExceededError,
    handleValidationErrors,
    handleProductOutOfStockError,
    handleLocationClosedError,
    isLineItemModificationInvalid,
    handleLineItemModificationInvalidError,
    isLoyaltyScanTokenExpired,
    handleLoyaltyScanTokenExpiredError,
    isOrderLocked,
    handleOrderLockedError,
    isUnknownError,
    handleUnknownError,
  ]);
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  genericError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'General | Error',
  },
  loyaltySessionExpired: {
    defaultMessage: 'Your loyalty session is expired',
    description: 'Cart Mutation Error | Loyalty session expired',
  },
  orderLocked: {
    defaultMessage: 'Cannot add item because group order is locked while owner is at checkout.',
    description: 'Cart Mutation Error | Group order locked',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LineItemErrorResponseData =
  | Readonly<{
      __typename?:
        | AddLineItemToCartSuccess['__typename']
        | EditLineItemInCartSuccess['__typename']
        | string;
      message?: string;
      fieldErrors?: ReadonlyArray<
        Readonly<{
          __typename?: 'FieldValidationError';
          field: string;
          message: string;
        }>
      >;
    }>
  | undefined;
